/**
 * Button <uo-button>
 *
 * Attributes:
 * disabled  Disables the button.
 * loading   Adds loading animation to the button. Disables the button.
 * color     Color of the button. "secondary", "danger".
 * size      Size of the button. "small", "extra-small".
 * href      The hyperlink of the button.
 * target    The target of the button.
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { isEmpty } from '@automator/utilities';
import { confirm } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'uo-button' )
export class AutomatorButton extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Focus shadow elements when elements in the light DOM are focused
	static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true
	}

	// Properties
	@property( { type: String } ) accessor color;

	// Defines if we have to render a gosh or filled button
	@property( { type: Boolean, reflect: true } ) accessor filled;

	@property( { type: String } ) accessor size;

	@property( { type: String } ) accessor href;

	@property( { type: String } ) accessor target;

	@property( { type: String } ) accessor type;

	@property( { type: Boolean, reflect: true } ) accessor disabled;

	@property( { type: Boolean, reflect: true } ) accessor loading;

	// Check if the action should be confirmed by the user
	// Doesn't work for custom event listeners. Only for anchor usage, and for forms
	@property( { type: Boolean, attribute: 'needs-confirmation' } ) accessor needsConfirmation = false;

	@property( { type: String, attribute: 'confirmation-heading' } ) accessor confirmationHeading;

	@property( { type: String, attribute: 'confirmation-content' } ) accessor confirmationContent;

	@property( { type: String, attribute: 'confirmation-button-label' } ) accessor confirmationButtonLabel;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<button
				size=${ ifDefined( this.size ) }
				color=${ ifDefined( this.color ) }
				type=${ ifDefined( this.type ) }

				?disabled=${ this.disabled || this.loading }
				?loading=${ this.loading }
				?filled=${ this.filled }

				tabindex="0"

				@click=${ event => this._handleClick( event ) }
			>
				<slot></slot>
			</button>
		`;
	}

	/**
	 * Handles clicks made to the component
	 *
	 * @param {PointerEvent} event The event
	 * @return {undefined}
	 */
	async _handleClick( event ) {
		// Ask the user to confirm the action first
		if ( this.needsConfirmation ) {
			try {
				await confirm( {
					heading: this.confirmationHeading,
					content: this.confirmationContent,
					confirmButtonLabel: this.confirmationButtonLabel
				} );
			} catch ( e ) {
				event.preventDefault();
				event.stopPropagation();

				// Stop here
				return;
			}
		}

		// Check if it's disabled and prevent the event
		if ( this.disabled ) {
			event.preventDefault();
			event.stopPropagation();
		}

		// Make sure the button is not disabled
		if ( ! this.disabled ) {
			// Check if it has a hyperlink defined
			if ( ! isEmpty( this.href ) ) {
				// The user added a link, we have to send him there
				// Simulate link
				const anchor = document.createElement( 'a' );

				// Set hyperlink
				anchor.setAttribute( 'href', this.href );

				// Check if it has a target defined
				if ( ! isEmpty( this.target ) ) {
					// Add target
					anchor.setAttribute( 'target', this.target );
				}

				// Simulate click
				anchor.click();
			}

			// Check if it has a type defined
			if ( ! isEmpty( this.type ) ) {
				// Check if the user wants to submit a form
				if ( this.type == 'submit' ) {
					// Get the form
					const $form = this.closest( 'form' );

					// Create a fake submit button
					const $submitButton = document.createElement( 'button' );
					$submitButton.setAttribute( 'type', 'submit' );

					// Add it to the form
					$form.appendChild( $submitButton );

					// Trigger a click
					$submitButton.click();

					// Remove the button
					$form.removeChild( $submitButton );
				}
			}
		}
	}
}
